import { makeObservable, observable, action, computed, ObservableMap } from 'mobx';

import { Store } from '../../store/store';
import { Message, StoreNode } from '../../store';
import { Reaction } from '../../entities';

export enum ReactionAnimations {
  BloomAnimation = 'BloomAnimation',
  FlameAnimation = 'FlameAnimation',
}

const Broadcast = {
  'ReactionBuffer:bloomAnimation': ReactionAnimations.BloomAnimation,
  'ReactionBuffer:reactionAnimation': ReactionAnimations.FlameAnimation,
} as const;

type AnimationParams = {
  bloomPosition?: number;
  flameTraj?: number;
};

type AnimatedReaction = {
  reaction: Reaction;
  animationParams: AnimationParams;
};

type Reactions = {
  [key in ReactionAnimations]: ObservableMap<string, AnimatedReaction>;
};

function isBroadcastKey(value: string): value is keyof typeof Broadcast {
  return value in Broadcast;
}

export class PlayerReactionAnimationsState extends StoreNode {
  constructor(store: Store) {
    super(store);
    makeObservable(this, {
      flameTrajCount: observable,
      bloomPositionCount: observable,
      bloomReactions: computed,
      flameReactions: computed,
      mounted: action,
      unmounted: action,
      handleBlur: action,
      reset: action,
      receiver: action,
      addBloomReaction: action,
      addFlameReaction: action,
      removeReaction: action,
    });
  }

  readonly maxFlameTraj: number = 10;
  readonly maxBloomPosition: number = 5;

  readonly reactions: Reactions = {
    [ReactionAnimations.BloomAnimation]: observable.map<
      string,
      AnimatedReaction
    >(),
    [ReactionAnimations.FlameAnimation]: observable.map<
      string,
      AnimatedReaction
    >(),
  };

  flameTrajCount: number = 0;
  bloomPositionCount: number = 0;

  get bloomReactions() {
    return [...this.reactions[ReactionAnimations.BloomAnimation].values()];
  }

  get flameReactions() {
    return [...this.reactions[ReactionAnimations.FlameAnimation].values()];
  }

  mounted() {
    this.reset();
    this.receive(this.receiver.bind(this), Object.keys(Broadcast));
  }

  unmounted() {
    this.reset();
  }

  handleBlur() {
    this.reset();
  }

  reset() {
    this.reactions[ReactionAnimations.BloomAnimation].clear();
    this.reactions[ReactionAnimations.FlameAnimation].clear();
  }

  receiver(msg: Message) {
    const { payload, fullType } = msg;

    const reaction: Reaction = payload.reaction;
    if (!isBroadcastKey(fullType))
      return;

    const animationName = Broadcast[fullType];

    switch (animationName) {
      case ReactionAnimations.BloomAnimation:
        this.addBloomReaction(reaction);
        break;
      case ReactionAnimations.FlameAnimation:
      default:
        this.addFlameReaction(reaction);
        break;
    }
  }

  addBloomReaction(reaction: Reaction) {
    this.bloomPositionCount = this.bloomPositionCount % this.maxBloomPosition + 1;

    this.reactions[ReactionAnimations.BloomAnimation].set(reaction.id, {
      reaction,
      animationParams: { bloomPosition: this.bloomPositionCount },
    });
  }

  addFlameReaction(reaction: Reaction) {
    this.flameTrajCount = this.flameTrajCount % this.maxFlameTraj + 1;

    this.reactions[ReactionAnimations.FlameAnimation].set(reaction.id, {
      reaction,
      animationParams: { flameTraj: this.flameTrajCount },
    });
  }

  removeReaction(reactionId: string, animationName: ReactionAnimations) {
    this.reactions[animationName].delete(reactionId);
  }
}
